<template>
  <div
    class="certification-tile"
    :class="{ 'certification-tile__acctionable': !pendingReview }"
    @click="handleTileClick"
  >
    <div class="certification-tile__user-status">
      <CertStatus
        :status="certificationUserStatusText || ''"
        :showStatus="showStatus"
      />
    </div>
    <div class="certification-tile__wrapper">
      <div class="certification-tile__top">
        <div class="certification-tile__status">
          {{ statusVersionBeta }}
        </div>
      </div>
      <div class="certification-tile__badge">
        <img
          class="certification-tile__badge-logo"
          :src="certification.logo_image_url"
        />
      </div>
      <h3 class="certification-tile__title">
        {{ certification.name }}
      </h3>
      <CertificationDetails
        v-if="showDetails"
        :difficulty="certification.difficulty || ''"
        :timeLimit="timeCap"
        :questions="certification.amount_questions"
        :objectives="labObjectives"
      />

      <router-link
        v-if="attemptPassed"
        :to="{
          name: 'user-dashboard',
          params: { mode: 'certifications' },
        }"
        class="btn btn--secondary certification-tile__button"
        >{{ buttonText }}</router-link
      >

      <a
        v-if="(hasNoVouchers && !isInBeta) || (noUserStatus && !isInBeta)"
        :href="`//${checkoutUrl}?products=ine-ind-cert-icca-cloud-associate`"
        target="_blank"
        rel="noopener nofollow"
        class="btn btn--secondary certification-tile__button"
      >
        Shop vouchers
      </a>
      <a
        v-if="(hasNoVouchers && isInBeta) || (noUserStatus && isInBeta)"
        :href="'//info.ine.com/icca-certification/'"
        target="_blank"
        rel="noopener nofollow"
        class="btn btn--secondary certification-tile__button"
      >
        Learn more
      </a>
      <button
        v-if="showButton"
        @click="setCertificationAndGo()"
        class="btn btn--secondary certification-tile__button"
      >
        {{ buttonText }}
      </button>
      <span v-if="pendingReview" class="certification-tile__review-status">
        We're reviewing your exam. You'll get a notification when the results
        are in.</span
      >
    </div>
  </div>
</template>

<script>
import CertStatus from '@/components/properties/CertStatus.vue'
import CertificationDetails from '@/components/certifications/CertificationDetails.vue'
import CONSTANTS from '@/constants'
import {
  hasPassedAttempt,
  noVouchers,
  certInBeta,
  certificationUserStatus,
  canNotTakeExam,
  activeVouchers,
  lastAttempt,
  attemptInProgress,
  certLatestVersion,
  hasUserStatus,
} from '@/helpers'

export default {
  name: 'CertificationTile',
  data: () => {
    return {
      userStatusCert: '',
    }
  },
  components: {
    CertStatus,
    CertificationDetails,
  },
  props: {
    certification: {
      type: Object,
      required: true,
    },
    lab: {
      type: Object,
      required: false,
    },
    showDetails: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  computed: {
    noUserStatus() {
      return !hasUserStatus(this.certification)
    },
    attemptPassed() {
      const attempt = lastAttempt(this.certification)
      return hasPassedAttempt(attempt)
    },
    hasNoVouchers() {
      return noVouchers(this.certification)
    },
    isInBeta() {
      return certInBeta(this.certification)
    },
    statusVersionBeta() {
      const {
        status,
        latest_version: latestVersion, // check field name
      } = this.certification

      if (this.isInBeta) return `v${latestVersion} BETA`

      if (status === CONSTANTS.CERTIFICATIONS.STATUS.COMING_SOON) {
        return CONSTANTS.CERTIFICATIONS.STATUS.COMING_SOON.replace(/_/g, ' ')
      }

      return `v${latestVersion}`
    },
    learningPath() {
      return {
        learning_areas: [this.certification.learning_area],
        ...this.certification.learning_paths[0],
      }
    },
    buttonText() {
      let text = null
      if (
        this.userStatusCert ===
        CONSTANTS.CERTIFICATIONS.USER_STATUS.ATTEMPT_IN_PROGRESS
      ) {
        text = 'Resume exam'
      } else if (
        this.userStatusCert ===
        CONSTANTS.CERTIFICATIONS.USER_STATUS.CAN_DO_RETAKE
      ) {
        text = 'Retake exam'
      } else if (
        this.userStatusCert === CONSTANTS.CERTIFICATIONS.USER_STATUS.AWARDED
      ) {
        text = 'Go to certification'
      } else if (
        this.userStatusCert === CONSTANTS.CERTIFICATIONS.USER_STATUS.NO_VOUCHERS
      ) {
        text = 'Shop vouchers'
      } else if (
        this.userStatusCert ===
        CONSTANTS.CERTIFICATIONS.USER_STATUS.READY_FOR_FIRST_ATTEMPT
      ) {
        text = 'Start Exam'
      } else if (
        this.userStatusCert ===
        CONSTANTS.CERTIFICATIONS.USER_STATUS.NOT_AVAILABLE
      ) {
        text = 'Learn more'
      }
      return text
    },
    showStatus() {
      return (
        this.userStatusCert ===
          CONSTANTS.CERTIFICATIONS.USER_STATUS.ATTEMPT_IN_REVIEW ||
        this.userStatusCert === CONSTANTS.CERTIFICATIONS.USER_STATUS.AWARDED
      )
    },
    showButton() {
      return (
        !canNotTakeExam(this.certification) &&
        this.userStatusCert !==
          CONSTANTS.CERTIFICATIONS.USER_STATUS.ATTEMPT_IN_REVIEW &&
        this.userStatusCert !== CONSTANTS.CERTIFICATIONS.USER_STATUS.AWARDED &&
        this.userStatusCert !==
          CONSTANTS.CERTIFICATIONS.USER_STATUS.NOT_AVAILABLE
      )
    },
    certificationUserStatusText() {
      return this.userStatusCert.replace(/_/g, ' ')
    },
    checkoutUrl() {
      return process.env.VUE_APP_CHECKOUT_URL
    },
    pendingReview() {
      return (
        this.userStatusCert ===
        CONSTANTS.CERTIFICATIONS.USER_STATUS.ATTEMPT_IN_REVIEW
      )
    },
    timeCap() {
      const certVersion = certLatestVersion(this.certification)
      const durationInSecs = certVersion
        ? certVersion.time_cap_in_secs
        : this.certification.time_cap_in_secs
      return this.$duration.durationFromSecs(durationInSecs, 'HOURSMIN')
    },
    labObjectives() {
      return this.lab ? this.lab.lab_objectives?.length : 0
    },
  },
  methods: {
    setCertificationAndGo() {
      this.$store.dispatch(
        'certifications/updateCurrentCertification',
        this.certification
      )
      const attempt = lastAttempt(this.certification)
      this.$router.push({
        name: 'certification-exam',
        params: {
          voucherId: activeVouchers(this.certification)[0].id,
          attemptId: attemptInProgress(attempt)
            ? attemptInProgress(attempt).id
            : null,
        },
      })
    },
    handleTileClick() {
      if (this.attemptPassed) {
        this.$router.push({
          name: 'user-dashboard',
          params: { mode: 'certifications' },
        })
      }
      if (this.hasNoVouchers && !this.isInBeta) {
        window.location.assign(
          `//${this.checkoutUrl}?products=ine-ind-cert-icca-cloud-associate`
        )
      }
      if (this.hasNoVouchers && this.isInBeta) {
        window.location.assign('//info.ine.com/icca-certification/')
      }
      if (this.showButton) this.setCertificationAndGo()
    },
  },
  mounted() {
    this.userStatusCert = this.certification.awarded
      ? CONSTANTS.CERTIFICATIONS.USER_STATUS.AWARDED
      : certificationUserStatus(this.certification)
  },
}
</script>

<style lang="scss" scoped>
.certification-tile {
  $this: &;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  background: linear-gradient(
    104.99deg,
    rgba(62, 63, 73, 0.7) -13.91%,
    rgba(62, 63, 73, 0) 100.48%
  );
  border-radius: 10px;
  width: 100%;
  @include mobile {
    width: calc(50% - 10px);
  }
  @include tablet {
    width: calc(33% - 10px);
  }
  &__acctionable {
    cursor: pointer;
  }
  &__wrapper {
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 0px 30px 30px 30px;
    justify-content: space-between;
  }
  &:hover {
    transition: 0.3s;
    background-color: rgb(255 255 255 / 5%);
    #{$this}__button {
      border: 1px solid var(--btn-hover-border-color) !important;
    }
  }
  &__top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    align-self: flex-end;
  }
  &__status {
    position: relative;
    box-sizing: border-box;
    background: linear-gradient(
      111.02deg,
      rgba(18, 18, 18, 0.7) 3.86%,
      rgba(18, 18, 18, 0) 107.96%
    );
    border-radius: 20px;
    color: var(--white);
    text-transform: uppercase;
    padding: 9px 14px;
    font-size: 12px;
    line-height: 14px;
    &:before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: -1;
      margin: -2px; /* !importanté */
      border-radius: inherit; /* !importanté */
      background: linear-gradient(
        111.02deg,
        #3e3f49b2 3.86%,
        #3e3f4900 107.96%
      );
    }
  }
  &__badge {
    display: flex;
    justify-content: center;
    margin-top: 10px;
    &-logo {
      height: 200px;
    }
  }
  &__title {
    font-weight: bold;
    font-size: 20px;
    line-height: 26px;
    display: flex;
    align-items: flex-end;
    color: var(--white);
    margin-top: 20px;
  }
  &__description {
    font-size: 16px;
    line-height: 24px;
    color: var(--light-blue-gray);
  }
  &__button {
    align-self: flex-end;
  }
  &__review-status {
    color: var(--mid-gray);
  }
  &__review-status:before {
    content: 'Pending Review:';
    color: #ffffff;
  }
}
</style>
